import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api';
import { AppShell } from '../ui/AppShell';
import {
  Button,
  Card,
  ErrorBanner,
  Pill,
  Spinner,
  TextInput,
} from '../ui/primitives';
import { useWhoami } from '../lib/auth';

export function AccountPage() {
  const who = useWhoami();
  const qc = useQueryClient();
  const nav = useNavigate();

  const logoutMut = useMutation({
    mutationFn: () => api.post<unknown>('/api/auth/logout'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['whoami'] });
      nav('/login', { replace: true });
    },
  });

  return (
    <AppShell title="Account">
      {who.data?.state === 'authenticated' ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <h3 className="mb-2 text-sm font-semibold">Signed in as</h3>
            <p>
              <span className="font-medium">{who.data.username}</span>{' '}
              <Pill tone={who.data.role === 'owner' ? 'amber' : 'neutral'}>
                {who.data.role}
              </Pill>
            </p>
            <p className="mt-2 text-[11px] text-ink-muted">
              {who.data.hasPin
                ? 'PIN is set; destructive actions require it.'
                : 'No PIN set; destructive actions will prompt until you set one.'}
            </p>
            <div className="mt-4">
              <Button
                variant="danger"
                onClick={() => logoutMut.mutate()}
                disabled={logoutMut.isPending}
              >
                {logoutMut.isPending ? <Spinner /> : 'Sign out'}
              </Button>
            </div>
          </Card>
          <ChangePasswordCard />
          <ChangePinCard hasPin={who.data.hasPin} />
        </div>
      ) : (
        <Card>Not authenticated.</Card>
      )}
    </AppShell>
  );
}

function ChangePasswordCard() {
  const [currentPassword, setCurrent] = useState('');
  const [newPassword, setNew] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  const mut = useMutation({
    mutationFn: async () => {
      if (newPassword !== confirm) throw new Error('Passwords do not match.');
      await api.post('/api/auth/change-password', { currentPassword, newPassword });
    },
    onSuccess: () => {
      setError(null);
      setOk(true);
      setCurrent('');
      setNew('');
      setConfirm('');
    },
    onError: (err) => {
      setOk(false);
      setError(err instanceof Error ? err.message : String(err));
    },
  });

  return (
    <Card>
      <h3 className="mb-2 text-sm font-semibold">Change password</h3>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          setOk(false);
          mut.mutate();
        }}
      >
        <TextInput
          label="Current password"
          type="password"
          autoComplete="current-password"
          value={currentPassword}
          onChange={(e) => setCurrent(e.target.value)}
          name="current-password"
        />
        <TextInput
          label="New password"
          type="password"
          autoComplete="new-password"
          value={newPassword}
          onChange={(e) => setNew(e.target.value)}
          name="new-password"
        />
        <TextInput
          label="Confirm new password"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          name="confirm-password"
        />
        {error && <ErrorBanner>{error}</ErrorBanner>}
        {ok && <p className="text-xs text-pos">Password changed.</p>}
        <Button type="submit" disabled={mut.isPending}>
          {mut.isPending ? <Spinner /> : 'Change password'}
        </Button>
      </form>
    </Card>
  );
}

function ChangePinCard({ hasPin }: { hasPin: boolean }) {
  const qc = useQueryClient();
  const [currentPin, setCurrent] = useState('');
  const [newPin, setNew] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  const mut = useMutation({
    mutationFn: async () => {
      if (newPin !== confirm) throw new Error('PINs do not match.');
      const payload: { currentPin?: string; newPin: string } = { newPin };
      if (hasPin) payload.currentPin = currentPin;
      await api.post('/api/auth/change-pin', payload);
    },
    onSuccess: () => {
      setError(null);
      setOk(true);
      setCurrent('');
      setNew('');
      setConfirm('');
      void qc.invalidateQueries({ queryKey: ['whoami'] });
    },
    onError: (err) => {
      setOk(false);
      if (err instanceof ApiError) setError(err.message);
      else setError(err instanceof Error ? err.message : String(err));
    },
  });

  return (
    <Card>
      <h3 className="mb-2 text-sm font-semibold">
        {hasPin ? 'Change PIN' : 'Set PIN'}
      </h3>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          setOk(false);
          mut.mutate();
        }}
      >
        {hasPin && (
          <TextInput
            label="Current PIN"
            type="password"
            value={currentPin}
            onChange={(e) => setCurrent(e.target.value)}
            name="current-pin"
          />
        )}
        <TextInput
          label="New PIN (4–64 chars)"
          type="password"
          value={newPin}
          onChange={(e) => setNew(e.target.value)}
          name="new-pin"
        />
        <TextInput
          label="Confirm new PIN"
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          name="confirm-pin"
        />
        {error && <ErrorBanner>{error}</ErrorBanner>}
        {ok && <p className="text-xs text-pos">PIN updated.</p>}
        <Button type="submit" disabled={mut.isPending}>
          {mut.isPending ? <Spinner /> : hasPin ? 'Change PIN' : 'Set PIN'}
        </Button>
      </form>
    </Card>
  );
}
